// ── Phase 4. Export / Import: the reproduction record ──────────────
//
// The cache is for speed and the export is for reproduction. They are not the
// same artefact and must not be collapsed: the cache is keyed on content and
// holds no arrangement at all, while a record is worthless without one.
//
// What travels is a PAIR. A reading and the arrangement it was frozen against
// are one unit (3-3's signature), and 2-4 keys the cache on content, so the
// same reading can legitimately appear against two different arrangements.
// Exporting readings and arrangements as separate lists would let an importer
// re-pair them wrongly and produce divergence figures nobody measured.

// Extension included on purpose. Every other module under src/ is standalone,
// so the verify scripts can import any one of them directly under node, and
// node's ESM resolver does not guess extensions the way webpack does. This is
// the first cross-module import here; without the .js it loads in the browser
// build and throws in the scripts, which is how it was found.
import { CLIENT_VERSION } from "./version.js";

export const EXPORT_FORMAT = "intuition-canvas/record";
export const EXPORT_FORMAT_VERSION = 1;

// The legacy test. NOT protocolVersion — a failed v6 attempt can legitimately
// carry no protocol version, because the server may never have answered, and
// filing those as pre-v6 data would put today's failures in the wrong era.
// Every v6-era entry has `status`; nothing written before v6 has the field.
export const isLegacy = (entry) => !entry || typeof entry.status !== "string";

// Legacy entries are LABELLED, not rewritten. The label lives on the block, so
// the entries themselves are exported byte-for-byte as they were recorded — a
// record that gets edited on the way out is not a record. Their `weight` is
// from the stored-value era and is not comparable with a derived resonance
// (Phase 1); the note says so rather than leaving the reader to find out.
export const LEGACY_NOTE =
  "Written before v6. These entries carry no protocolVersion and no status, " +
  "their `weight` is the stored value from before resonance became derived, " +
  "and they hold no frozen arrangement. Not comparable with the records above. " +
  "Exported unmodified.";

export function buildExport(log, { includeLegacy = true, exportedAt, sessionName = "" } = {}) {
  const entries = Array.isArray(log) ? log : [];
  const v6 = entries.filter(e => !isLegacy(e));
  const legacy = entries.filter(isLegacy);

  // The instrument stamp comes from the newest entry that actually carries one.
  // A failed attempt may have none, so this looks past those rather than
  // reporting null for a session that did produce readings.
  const stamped = [...v6].reverse().find(e => e.protocolVersion && e.modelVersion);

  return {
    format: EXPORT_FORMAT,
    formatVersion: EXPORT_FORMAT_VERSION,
    exportedAt: exportedAt || new Date().toISOString(),
    instrument: {
      protocolVersion: stamped?.protocolVersion || null,
      modelVersion: stamped?.modelVersion || null,
      // What the arranging was done on. A constant here because this build IS
      // the screen instrument; the table prototype writes its own records and
      // says "table". It is stated rather than inferred: the reader defaulted
      // this to "screen" when the export was silent, which would have gone on
      // reporting screen for a table session — a provenance field whose value
      // comes from the reader is not provenance.
      surface: "screen",
      // Which client build did the arranging. protocolVersion cannot say:
      // v6.1 did not touch api/mirror.js, so a v6.1 record and a v6.0.2 record
      // carry the same v6.0.2 stamp. Without this, "session 02 ran on v6.1" is
      // an inference from start:"open" rather than something the file states —
      // and the portfolio meta line (SESSION 02 · OPEN START · v6.1.0) would
      // have no source in the record it describes.
      clientVersion: CLIENT_VERSION,
    },
    // The session's own name for itself, asked for after every reading was
    // made. It lives here rather than on a record because it is about the
    // sitting, not about one arrangement — and because a record is written at
    // the freeze, which is before the question is allowed to be asked.
    //
    // Empty stays empty. An unanswered question is not the same as one that
    // was never put, and a default like "untitled" would erase the difference.
    session: { name: sessionName || null },
    // 5-1 reads this. Failures and cache hits are part of the record, not
    // noise: without the failures there is no denominator, and a cache hit is
    // a reading the user saw. The counts are stated even when a block is left
    // out, so an omission can never read as an absence.
    counts: {
      ok: v6.filter(e => e.status === "ok").length,
      failed: v6.filter(e => e.status === "failed").length,
      cached: v6.filter(e => e.status === "cached").length,
      legacy: legacy.length,
    },
    // Each entry already carries its own `frozen` snapshot and its `output`,
    // so the pair is intact per record and cannot be mis-paired downstream.
    records: v6,
    legacy: {
      count: legacy.length,
      protocolVersion: "pre-v6",
      included: includeLegacy,
      note: LEGACY_NOTE,
      entries: includeLegacy ? legacy : null,
    },
  };
}

export function serializeExport(record) {
  return JSON.stringify(record, null, 2);
}

export function exportFilename(record) {
  const day = (record.exportedAt || "").slice(0, 10) || "undated";
  return `intuition-canvas-${day}.json`;
}

// ── import ─────────────────────────────────────────────────────────

export function parseImport(text) {
  let data;
  try { data = JSON.parse(text); }
  catch { throw new Error("Not a JSON file."); }
  if (!data || data.format !== EXPORT_FORMAT) {
    throw new Error("Not an Intuition Canvas record.");
  }
  if (data.formatVersion > EXPORT_FORMAT_VERSION) {
    throw new Error(
      `This record is format ${data.formatVersion}; this build reads ${EXPORT_FORMAT_VERSION}. ` +
      "Refusing rather than guessing at fields it does not know."
    );
  }
  if (!Array.isArray(data.records)) throw new Error("Record has no readings.");
  return data;
}

// The readings that can actually be put back on a canvas: a reading was
// produced and an arrangement was frozen with it. A failed attempt has a
// snapshot but no output, so it is kept in the file as evidence and is not
// offered as something to restore.
export function restorableRecords(record) {
  return (record.records || []).filter(
    e => e.output && e.frozen && e.frozen.positions && e.frozen.field
  );
}

// Put one record back on THIS canvas. The frozen snapshot is normalised, so
// the arrangement reproduces on any window — that is what 3-1 stored the field
// for. Pixels are derived here; the snapshot travels unchanged, so the
// restored pair measures identically to the one that was exported.
export function restoreFragments(entry, field) {
  const byId = new Map((entry.fragments || []).map(f => [f.id, f]));
  return Object.entries(entry.frozen.positions).map(([id, p]) => {
    const f = byId.get(id) || {};
    return {
      id,
      text: f.text || "",
      type: f.type || "text",
      named: !!(f.text && f.text.length),
      // Provenance is a fact about the session that made the fragment, so it
      // rides back in unchanged. Records written before v6.1 carry none of it
      // and restore as null — unknown, which is what they are.
      origin: f.origin ?? null,
      initialPosition: f.initialPosition ?? null,
      createdAt: f.createdAt ?? null,
      // The in-session counter starts at zero because this session has not
      // moved it yet. `imported` is what stops the export reporting that zero
      // as if it were the fragment's whole history.
      moves: 0,
      // The snapshot's own normalised values, carried across unchanged. A
      // restored record re-frozen on a different screen has to produce the
      // arrangement it was exported with, and going back out through this
      // window's pixels would lose the last bit or two.
      nx: p.nx, ny: p.ny,
      x: p.nx * field.w,
      y: p.ny * field.h,
      imported: true,
    };
  });
}
